import React, { useState, useEffect, useContext } from 'react';
import { BrandingContext } from '../../../../BrandingProvider';
import { Box, Grid, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody, Typography, Tooltip } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { downloadPoPdfFromData, previewPoPdfInNewTab, fetchPoPdfData } from '../../utils/poPdf';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import LetterheadMenu, { useLetterheadMenu } from '../../components/LetterheadMenu';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DetailRow } from '../../components/InvField';
import { getPurchaseOrder, getPurchaseOrderItems, submitPoForApproval,
  approvePo, rejectPo, sendPoToVendor, cancelPo, closePo,
  deletePurchaseOrder } from '../../api/procurement';
import { getVendors } from '../../api/vendor';
import { getGrns } from '../../api/grn';
import useItemNames from '../../hooks/useItemNames';
import { formatDate, formatCurrency, formatQty, lineTotal } from '../../utils';
import { IPD_COLORS } from '../../theme';
import { toast } from 'react-toastify';
import { derivePoLifecycle, getPoAllowedActions, getPoActionBlockers, getPoDisplayStatus, hasDisplayValue } from '../../utils/poLifecycle';
import useInvRole from '../../hooks/useInvRole';
import { filterPoActionsByRole, PO_DIALOG_ACTION_PERMISSION } from '../../utils/inventoryRoles';

const NUM_CELL = {
  align: 'right',
  whiteSpace: 'nowrap',
  px: 1.5,
  verticalAlign: 'middle',
};

const MONEY_CELL = {
  ...NUM_CELL,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 13,
};

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.data || data?.items || []);
}

function vendorLabel(vendor) {
  if (!vendor) return null;
  return vendor.vendor_code ? `${vendor.vendor_name} (${vendor.vendor_code})` : vendor.vendor_name;
}

export default function PoDetail() {
  const { id } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const { resolveItem } = useItemNames();
  const brandingData = useContext(BrandingContext);
  const facilityLogo = (() => {
    const img = brandingData?.find(b => b.name === 'collateral_image')?.content;
    return img ? `data:image/png;base64,${img}` : null;
  })();
  const [po, setPo] = useState(null);
  const [poItems, setPoItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [grns, setGrns] = useState([]);
  const [grnsLoading, setGrnsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [actioning, setActioning] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const letterheadMenu = useLetterheadMenu();

  async function load() {
    setLoading(true); setError(null);
    try {
      // API: GET /inventory/purchase-orders/{id}/
      const p = await getPurchaseOrder(id);
      setPo(p);
      const scope = { tenant_id: p.tenant_id, hospital_id: p.hospital_id };
      const [items, vendorsData] = await Promise.all([
        // API: GET /inventory/purchase-order-items/ — filter by po_id
        getPurchaseOrderItems(id, scope).catch(() => []),
        // API: GET /inventory/vendors/
        getVendors().catch(() => []),
      ]);
      setPoItems(normalizeList(items));
      setVendors(normalizeList(vendorsData));
    } catch (e) { setError(e.message); } finally { setLoading(false); }

    setGrnsLoading(true);
    try {
      // API: GET /inventory/grns/ — filter by purchase_order_id
      const grnData = await getGrns({ purchase_order_id: id });
      setGrns(normalizeList(grnData));
    } catch { setGrns([]); } finally { setGrnsLoading(false); }
  }

  function resolveVendorName(vendorId) {
    if (!vendorId) return null;
    const vendor = vendors.find(v => (v.vendor_id || v.id) === vendorId);
    return vendorLabel(vendor) || vendorId;
  }

  useEffect(() => { load(); }, [id]);

  async function executeAction() {
    const required = PO_DIALOG_ACTION_PERMISSION[dialog.action];
    if (required && !can(required)) return;
    // The dialog vocabulary differs from the lifecycle keys ('short-close' vs 'short_close').
    const blocked = getPoActionBlockers(po, poItems, grns)[dialog.action.replace('-', '_')];
    if (blocked) { toast.error(blocked); setDialog(null); return; }
    setActioning(true);
    try {
      const scope = { tenant_id: po?.tenant_id, hospital_id: po?.hospital_id };
      const remarks = reason || undefined;
      // API: POST /inventory/purchase-orders/?action=submit-for-approval
      if (dialog.action === 'submit') await submitPoForApproval(id, { ...scope, remarks });
      // API: POST /inventory/purchase-orders/?action=approve
      else if (dialog.action === 'approve') await approvePo(id, { ...scope, remarks });
      // API: POST /inventory/purchase-orders/?action=reject
      else if (dialog.action === 'reject') await rejectPo(id, { ...scope, reason, remarks });
      // API: POST /inventory/purchase-orders/?action=send-to-vendor
      else if (dialog.action === 'send') await sendPoToVendor(id, { ...scope, remarks });
      // API: POST /inventory/purchase-orders/?action=cancel
      else if (dialog.action === 'cancel') await cancelPo(id, { ...scope, reason, remarks });
      // API: POST /inventory/purchase-orders/{id}/close/
      else if (dialog.action === 'close') await closePo(id, { ...scope, remarks });
      // API: POST /inventory/purchase-orders/{id}/close/ with force_close — short-close with pending qty
      else if (dialog.action === 'short-close') await closePo(id, { ...scope, reason, force_close: true });
      // API: DELETE /inventory/purchase-orders/{id}/
      else if (dialog.action === 'delete') {
        await deletePurchaseOrder(id, scope);
        setDialog(null);
        setReason('');
        toast.success('Purchase order deleted');
        history.push(url.replace(/\/po\/.*/, '/pos'));
        return;
      }
      setDialog(null); setReason(''); load();
      if (dialog.action === 'approve') toast.success('Purchase order approved');
      else if (dialog.action === 'reject') toast.success('Purchase order rejected');
    } catch (e) {
      toast.error(e?.message || 'Action failed');
    } finally { setActioning(false); }
  }

  async function handleViewPdf(letterhead) {
    setPdfLoading(true);
    try {
      const data = await fetchPoPdfData(id, { tenant_id: po?.tenant_id, hospital_id: po?.hospital_id }, { facilityLogo, letterhead });
      previewPoPdfInNewTab(data);
    } catch (e) { console.error('PO PDF preview failed', e); }
    finally { setPdfLoading(false); }
  }

  async function handleDownloadPdf(letterhead) {
    setPdfLoading(true);
    try {
      const data = await fetchPoPdfData(id, { tenant_id: po?.tenant_id, hospital_id: po?.hospital_id }, { facilityLogo, letterhead });
      downloadPoPdfFromData(data);
    } catch (e) { console.error('PO PDF download failed', e); }
    finally { setPdfLoading(false); }
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress sx={{ color: IPD_COLORS.primary }} /></Box>;
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  if (!po) return null;

  const computedTotal = poItems.reduce((sum, l) =>
    sum + lineTotal(l.ordered_quantity, l.unit_rate, l.tax_percent, l.discount_percent), 0);
  const grandTotal = poItems.length > 0
    ? computedTotal
    : (Number(po.total_net_amount) || Number(po.total_basic_amount) || 0);

  const lifecycle = derivePoLifecycle(po, poItems, grns);
  const allowedActions = filterPoActionsByRole(getPoAllowedActions(po, poItems, grns), can);
  const blockers = getPoActionBlockers(po, poItems, grns);
  const displayStatus = getPoDisplayStatus(po, poItems, grns);
  const actionButtons = [];
  if (allowedActions.has('submit')) actionButtons.push(
    <InvButton key="submit" size="small" tone="mint"
      onClick={() => setDialog({ action: 'submit', title: 'Submit for Approval', desc: 'Submit PO for approval?' })}>
      Submit for Approval
    </InvButton>
  );
  if (allowedActions.has('delete')) actionButtons.push(
    <InvButton key="delete" size="small" tone="red" variant="outlined"
      onClick={() => setDialog({
        action: 'delete',
        title: 'Delete Purchase Order',
        desc: 'Delete this purchase order? It will be deactivated and removed from the list.',
        destructive: true,
      })}>
      Delete
    </InvButton>
  );
  if (allowedActions.has('approve') || allowedActions.has('reject')) {
    actionButtons.push(
      allowedActions.has('approve') ? (
        <InvButton key="approve" size="small"
          onClick={() => setDialog({ action: 'approve', title: 'Approve PO', desc: 'Approve this purchase order?' })}>
          Approve
        </InvButton>
      ) : null,
      allowedActions.has('reject') ? (
        <InvButton key="reject" size="small" tone="red" variant="outlined"
          onClick={() => setDialog({ action: 'reject', title: 'Reject PO', desc: 'Reject this PO?', destructive: true, requireReason: true })}>
          Reject
        </InvButton>
      ) : null
    );
  }
  if (allowedActions.has('send_to_vendor')) actionButtons.push(
    <InvButton key="send" size="small" tone="mint"
      onClick={() => setDialog({ action: 'send', title: 'Send to Vendor', desc: 'Send this PO to the vendor?' })}>Send to Vendor</InvButton>
  );
  if (allowedActions.has('create_grn')) actionButtons.push(
    <InvButton key="grn" size="small" variant="outlined" tone="teal"
      onClick={() => history.push(`${url.replace(/\/po\/.*/, '/grn/new')}?po=${id}`)}>Create GRN</InvButton>
  );
  if (allowedActions.has('close')) actionButtons.push(
    <InvButton key="close" size="small" tone="mint" variant="outlined"
      onClick={() => setDialog({
        action: 'close', title: 'Close PO',
        desc: 'All items have been fully received. Close this purchase order?',
      })}>
      Close PO
    </InvButton>
  );
  if (allowedActions.has('short_close')) actionButtons.push(
    <Tooltip key="short-close" title={blockers.short_close || ''}>
      <span>
        <InvButton size="small" tone="red" variant="outlined"
          disabled={Boolean(blockers.short_close)}
          onClick={() => setDialog({
            action: 'short-close', title: 'Short Close PO',
            desc: 'Some items still have pending quantity. Short-closing writes off the remaining balance without waiting for further delivery — a reason is required.',
            destructive: true, requireReason: true,
          })}>
          Short Close PO
        </InvButton>
      </span>
    </Tooltip>
  );
  if (allowedActions.has('cancel')) actionButtons.push(
    <Tooltip key="cancel" title={blockers.cancel || ''}>
      <span>
        <InvButton size="small" tone="red" variant="outlined"
          disabled={Boolean(blockers.cancel)}
          onClick={() => setDialog({
            action: 'cancel',
            title: lifecycle.effectiveStatus === 'PARTIALLY_RECEIVED' ? 'Cancel Remaining Quantity' : 'Cancel PO',
            desc: lifecycle.effectiveStatus === 'PARTIALLY_RECEIVED'
              ? 'Only the remaining quantity can be cancelled. Continue?'
              : 'Cancel this PO?',
            destructive: true,
            requireReason: true,
          })}>
          {lifecycle.effectiveStatus === 'PARTIALLY_RECEIVED' ? 'Cancel Remaining' : 'Cancel'}
        </InvButton>
      </span>
    </Tooltip>
  );
  if (allowedActions.has('audit')) actionButtons.push(
    <InvButton key="audit" size="small" variant="outlined" tone="teal"
      onClick={() => toast.info('Audit history is available from backend logs for this PO.')}>
      Audit History
    </InvButton>
  );

  return (
    <Box>
      <PageHeader eyebrow="Material Management › Purchase Orders" title={po.po_number}
        actions={
          <>
            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.goBack()}>Back</InvButton>
            <InvButton variant="outlined" tone="teal" size="small"
              startIcon={<PictureAsPdfIcon />} disabled={pdfLoading}
              onClick={(e) => letterheadMenu.open(e, handleViewPdf)}>
              {pdfLoading ? 'Generating…' : 'View PDF'}
            </InvButton>
            <InvButton variant="outlined" tone="teal" size="small"
              startIcon={<DownloadIcon />} disabled={pdfLoading}
              onClick={(e) => letterheadMenu.open(e, handleDownloadPdf)}>
              Download PDF
            </InvButton>
            <LetterheadMenu menu={letterheadMenu} label="Purchase Order" />
            {actionButtons}
          </>
        }
      />
      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid item xs={12} md={5}>
          <SectionCard title="PO Details" sx={{ height: '100%' }} actions={
            displayStatus ? <StatusChip status={displayStatus} /> : null
          }>
            <DetailRow label="Vendor">{resolveVendorName(po.vendor_id)}</DetailRow>
            <DetailRow label="Expected">{formatDate(po.expected_delivery_date)}</DetailRow>
            {hasDisplayValue(po.payment_terms) && (
              <DetailRow label="Payment Terms">{po.payment_terms}</DetailRow>
            )}
            {hasDisplayValue(po.delivery_address) && (
              <DetailRow label="Delivery Address">{po.delivery_address}</DetailRow>
            )}
            {hasDisplayValue(po.remarks) && (
              <DetailRow label="Remarks">{po.remarks}</DetailRow>
            )}
            <DetailRow label="Created">{formatDate(po.created_at)}</DetailRow>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={7}>
          <SectionCard title="Line Items" sx={{ height: '100%' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{
                minWidth: 620,
                tableLayout: 'fixed',
                width: '100%',
                '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary, py: 1.25 },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: '26%' }}>Item</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, ...NUM_CELL, width: '10%' }}>Ordered</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, ...NUM_CELL, width: '10%' }}>Accepted</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, ...NUM_CELL, width: '10%' }}>Pending</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, ...MONEY_CELL, width: '14%' }}>Rate</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, ...NUM_CELL, width: '8%' }}>Tax%</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, ...NUM_CELL, width: '8%' }}>Disc%</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, ...MONEY_CELL, width: '14%' }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {poItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary, py: 3 }}>
                        No line items
                      </TableCell>
                    </TableRow>
                  ) : poItems.map((item, i) => {
                    const pending = item.pending_quantity != null
                      ? Math.floor(Number(item.pending_quantity) || 0)
                      : null;
                    return (
                    <TableRow key={item.id || i}>
                      <TableCell sx={{ wordBreak: 'break-word', pr: 2 }}>
                        {resolveItem(item)}
                      </TableCell>
                      <TableCell sx={NUM_CELL}>{formatQty(item.ordered_quantity)}</TableCell>
                      <TableCell sx={{ ...NUM_CELL, color: IPD_COLORS.success.text }}>
                        {item.received_quantity != null ? formatQty(item.received_quantity) : '—'}
                      </TableCell>
                      <TableCell sx={{
                        ...NUM_CELL,
                        color: pending === 0 ? IPD_COLORS.textSecondary : IPD_COLORS.warning.text,
                        fontWeight: pending > 0 ? 600 : 400,
                      }}>
                        {pending != null ? formatQty(pending) : '—'}
                      </TableCell>
                      <TableCell sx={MONEY_CELL}>{formatCurrency(item.unit_rate)}</TableCell>
                      <TableCell sx={NUM_CELL}>{item.tax_percent || 0}%</TableCell>
                      <TableCell sx={NUM_CELL}>{item.discount_percent || 0}%</TableCell>
                      <TableCell sx={{ ...MONEY_CELL, fontWeight: 600 }}>
                        {formatCurrency(
                          item.line_total != null
                            ? item.line_total
                            : lineTotal(item.ordered_quantity, item.unit_rate, item.tax_percent, item.discount_percent)
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="right"
                      sx={{
                        fontWeight: 700,
                        color: IPD_COLORS.primary,
                        borderTop: `2px solid ${IPD_COLORS.border}`,
                        pr: 2,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Grand Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...MONEY_CELL,
                        fontWeight: 700,
                        color: IPD_COLORS.primary,
                        borderTop: `2px solid ${IPD_COLORS.border}`,
                      }}
                    >
                      {formatCurrency(grandTotal)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      <SectionCard title="GRN History" sx={{ mt: 2.5 }}>
        {grnsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} sx={{ color: IPD_COLORS.primary }} />
          </Box>
        ) : grns.length === 0 ? (
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13, py: 1 }}>
            No GRNs have been created against this purchase order yet.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>GRN</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>GRN Date</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Status</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {grns.map((g) => {
                  const grnId = g.grn_id || g.id;
                  return (
                    <TableRow key={grnId}>
                      <TableCell>{g.grn_number}</TableCell>
                      <TableCell>{formatDate(g.grn_date)}</TableCell>
                      <TableCell><StatusChip status={g.posting_status || g.status} /></TableCell>
                      <TableCell>
                        <InvButton size="small" variant="outlined" tone="teal"
                          onClick={() => history.push(url.replace(/\/po\/.*/, `/grn/${grnId}`))}>
                          View
                        </InvButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </SectionCard>

      <ConfirmDialog open={Boolean(dialog)} title={dialog?.title} description={dialog?.desc}
        destructive={dialog?.destructive} requireReason={dialog?.requireReason}
        reasonValue={reason} onReasonChange={setReason} loading={actioning}
        onConfirm={executeAction} onCancel={() => { setDialog(null); setReason(''); }} />
    </Box>
  );
}
